import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { ApiKeyCreationResponse } from '../services/api';

interface Props {
  keyData: ApiKeyCreationResponse | null;
  onClose: () => void;
}

const RevealApiKeyDialog: React.FC<Props> = ({ keyData, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = async () => {
    if (!keyData) return;
    try {
      await navigator.clipboard.writeText(keyData.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard may be unavailable in some browsers / contexts; the user can
      // still select the value manually.
    }
  };

  const handleClose = () => {
    setCopied(false);
    setAcknowledged(false);
    onClose();
  };

  const open = !!keyData;

  return (
    <Dialog open={open} onClose={() => { /* no-op: user must acknowledge */ }} maxWidth="sm" fullWidth>
      <DialogTitle>Your new API key</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning" sx={{ fontWeight: 500 }}>
            This is the only time you will see this key. Copy it now and store
            it somewhere secure (1Password, AWS Secrets Manager, etc.). If you
            lose it, you'll need to revoke it and create a new one.
          </Alert>

          {keyData && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Key name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {keyData.keyName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Scopes
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {keyData.scopes.map((scope) => (
                    <Chip key={scope} label={scope} size="small" />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Secret key (copy now)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.5 }}>
                  <TextField
                    value={keyData.key}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={3}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                      onClick={handleCopy}
                      color={copied ? 'success' : 'primary'}
                      sx={{ mt: 0.5 }}
                      aria-label="Copy API key"
                    >
                      {copied ? <CheckIcon /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Rate limit
                </Typography>
                <Typography variant="body2">
                  {keyData.rateLimit} requests per {keyData.rateLimitWindow}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setAcknowledged(true)}
          disabled={acknowledged}
          sx={{ display: acknowledged ? 'none' : 'inline-flex' }}
        >
          I've copied it somewhere safe
        </Button>
        <Button
          variant="contained"
          onClick={handleClose}
          disabled={!acknowledged}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevealApiKeyDialog;
